import { wasItHelpfulView } from '@wix/bi-logger-customer-care-data/v2';
import { useRouter } from 'next/router';
import { useEffect, useContext, useCallback } from 'react';

import { useBI } from './useBI';
import { useDuplexerChatbotInstance } from './useDuplexerChatbotInstance';
import useIsHelpCenterCopilotEnabled from './useIsHelpCenterCopilotEnabled';
import { Context } from '../context';
import { mapPageTypeToSourceName } from '../utils/bi';
import { useStore } from '../zustand/stores';

const CHATBOT_APP_DEF_ID = '2b5135e9-9dea-494d-b1d5-92a4be5c9034';
const DUPLEXER_CONNECTED_EVENT = '@duplexer:connected';
const CHATBOT_STATE_UPDATE_EVENT = 'CHATBOT_STATE_UPDATE';
const MESSAGE_READ = 'MESSAGE_READ';

export const useChatbotMessageCounter = (articleId: string) => {
  const { setUnreadMessagesCount } = useStore(state => state.copilotSlice);
  const duplexerInstance = useDuplexerChatbotInstance();
  const isHelpCenterCopilotEnabled = useIsHelpCenterCopilotEnabled();
  const { sendBIEvent } = useBI();
  const { locale } = useRouter();
  const { pageType } = useContext(Context);

  const sendNotificationViewEvent = useCallback(async () => {
    await sendBIEvent(
      wasItHelpfulView({
        source_name: mapPageTypeToSourceName(pageType),
        kb_lang: locale,
        item_id: articleId,
        item_name: 'article assistant live chat notification',
      })
    );
  }, [sendBIEvent, articleId, pageType, locale]);

  useEffect(() => {
    if (!isHelpCenterCopilotEnabled) {
      return;
    }

    const appConnection = duplexerInstance.connect({ appDefId: CHATBOT_APP_DEF_ID });

    appConnection.on(DUPLEXER_CONNECTED_EVENT, () => {
      appConnection.subscribeToUserChannel().on(CHATBOT_STATE_UPDATE_EVENT, (data: { unreadMessagesCount: number }) => {
        setUnreadMessagesCount(data.unreadMessagesCount);
        void sendNotificationViewEvent();
      });
      appConnection.subscribeToUserChannel().on(MESSAGE_READ, () => {
        setUnreadMessagesCount(0);
      });
    });

    return () => {
      appConnection.disconnect();
    };
  }, [setUnreadMessagesCount, duplexerInstance, isHelpCenterCopilotEnabled, sendNotificationViewEvent]);
};
